import { type CSSProperties, type ReactNode, useEffect } from 'react'
import { generateId } from '@ondit/utils/id'
import { useLocation } from 'react-router'
import { type ExternalToast, toast as sonner, Toaster } from 'sonner'
import { CircleAlert, CircleCheck, CircleInfo, Loader, TriangleAlert } from '../../icons'

/**
 * The Agent's toast API, drawn by the application's one sonner stack.
 *
 * This was a second notification system — its own stack, its own animation,
 * its own corner — and on a screen that holds both modules' code (the settings
 * page) a panel toast and an Agent toast piled up in the same corner as two
 * differently drawn cards. `ToastProvider` below is the one stack; what is left
 * here is the vocabulary the Agent's call sites already speak, translated into
 * sonner's.
 */

/** emcn's default, kept rather than sonner's 4 s so no call site reads for less time than it did. */
const AUTO_DISMISS_MS = 5000

type ToastVariant = 'default' | 'info' | 'success' | 'warning' | 'error'

interface ToastAction {
  label: string
  onClick: () => void
}

type ToastInput = {
  message: string
  description?: string
  variant?: ToastVariant
  action?: ToastAction
  /** Called once when the toast leaves the stack, regardless of how it was dismissed. */
  onDismiss?: () => void
  /** Milliseconds; `0` keeps the toast until it is dismissed. */
  duration?: number
  /** Reuse an id to replace a toast in place rather than stacking a second one. */
  id?: string
  /**
   * Keep the toast across navigation. Page-scoped notifications are otherwise
   * dismissed when the route changes, so they do not trail the reader onto a
   * screen they say nothing about; set this for ongoing-state toasts like a
   * connection status.
   * @default false
   */
  persistAcrossRoutes?: boolean
}

type ToastOptions = Omit<ToastInput, 'message' | 'variant'>

type ToastFn = {
  (input: ToastInput): string
  success: (message: string, options?: ToastOptions) => string
  error: (message: string, options?: ToastOptions) => string
  warning: (message: string, options?: ToastOptions) => string
  info: (message: string, options?: ToastOptions) => string
  /** Dismisses a single toast by id. */
  dismiss: (id: string) => void
  /** Dismisses every toast raised through this API. */
  dismissAll: () => void
}

/**
 * emcn's options in sonner's terms.
 *
 * An actionable toast waits for its reader unless the caller gave a duration:
 * a button that disappears before somebody reaches it is a button that was
 * never offered. sonner has no "forever" duration of zero — `0` there means
 * gone at once — so emcn's `0` becomes `Infinity`.
 */
export function toSonnerOptions(input: Omit<ToastInput, 'message' | 'variant'> & { id: string }) {
  const duration = input.duration ?? (input.action ? 0 : AUTO_DISMISS_MS)
  const options: ExternalToast = {
    id: input.id,
    description: input.description,
    duration: duration > 0 ? duration : Number.POSITIVE_INFINITY,
  }
  if (input.action) {
    const { label, onClick } = input.action
    // sonner dismisses the toast after the click, which is what emcn did.
    options.action = { label, onClick: () => onClick() }
  }
  return options
}

/**
 * The path each live toast was raised on, or `null` when it outlives
 * navigation.
 *
 * Read from `location` at creation rather than from the router's state: a
 * screen that raises a toast from its mount effect does so after the history
 * entry has changed but before any parent effect has seen the new path, and
 * recording the path there is what lets the sweep below leave that toast alone
 * while it clears the ones the previous screen left behind.
 */
const raisedOn = new Map<string, string | null>()

function currentPath(): string | null {
  return globalThis.location?.pathname ?? null
}

function forget(id: string, onDismiss?: () => void) {
  return () => {
    raisedOn.delete(id)
    onDismiss?.()
  }
}

function show(variant: ToastVariant, message: string, input: ToastOptions = {}): string {
  const id = input.id ?? generateId()
  raisedOn.set(id, input.persistAcrossRoutes ? null : currentPath())
  const options: ExternalToast = {
    ...toSonnerOptions({ ...input, id }),
    // sonner reports a timeout and a dismissal separately; emcn's callers were
    // promised one call however the toast left.
    onDismiss: forget(id, input.onDismiss),
    onAutoClose: forget(id, input.onDismiss),
  }
  if (variant === 'default') sonner(message, options)
  else sonner[variant](message, options)
  return id
}

function dismiss(id: string) {
  raisedOn.delete(id)
  sonner.dismiss(id)
}

/**
 * Dismisses every route-scoped toast raised anywhere but `pathname`.
 * Exported for the provider below and for its test.
 */
export function sweepRoute(pathname: string) {
  for (const [id, path] of raisedOn) {
    if (path !== null && path !== pathname) dismiss(id)
  }
}

/**
 * Imperative toast. A toast carrying an `action` persists until dismissed
 * unless an explicit `duration` is passed.
 *
 * @example
 * ```tsx
 * toast.error('Upload failed', { description: 'Network timed out' })
 * toast.success('Saved', { action: { label: 'View', onClick: () => navigate('/x') } })
 * ```
 */
export const toast: ToastFn = Object.assign(
  ({ message, variant = 'default', ...input }: ToastInput) => show(variant, message, input),
  {
    success: (message: string, options?: ToastOptions) => show('success', message, options),
    error: (message: string, options?: ToastOptions) => show('error', message, options),
    warning: (message: string, options?: ToastOptions) => show('warning', message, options),
    info: (message: string, options?: ToastOptions) => show('info', message, options),
    dismiss,
    dismissAll: () => {
      for (const id of [...raisedOn.keys()]) dismiss(id)
    },
  }
)

const handle = { toast, dismiss: toast.dismiss, dismissAll: toast.dismissAll }

/** The toast function and its dismiss helpers, for call sites written against the old context. */
export function useToast() {
  return handle
}

/**
 * Every colour is a token in an inline style rather than sonner's own palette:
 * `theme="system"` only picks which of sonner's built-ins it would have
 * painted, and an inline value is the only one that beats a stylesheet sonner
 * injects at runtime. So a toast matches the surface it sits on in either
 * scheme without a surface having to tell this component which scheme that is.
 */
const TOAST_STYLE = {
  '--normal-bg': 'var(--surface)',
  '--normal-text': 'var(--fg)',
  '--normal-border': 'var(--border-strong)',
  '--success-bg': 'var(--success-soft)',
  '--success-text': 'var(--fg)',
  '--success-border': 'var(--border-strong)',
  '--error-bg': 'var(--danger-soft)',
  '--error-text': 'var(--fg)',
  '--error-border': 'var(--border-strong)',
  '--warning-bg': 'var(--warning-soft)',
  '--warning-text': 'var(--fg)',
  '--warning-border': 'var(--border-strong)',
  '--border-radius': 'var(--radius)',
} as CSSProperties

const TOAST_ICONS = {
  success: <CircleCheck className='size-4' />,
  info: <CircleInfo className='size-4' />,
  warning: <TriangleAlert className='size-4' />,
  error: <CircleAlert className='size-4' />,
  loading: <Loader className='size-4 animate-spin' />,
}

/**
 * The one stack, plus emcn's promise that a page's toasts do not follow the
 * reader to the next page.
 *
 * It draws the stack because nothing else did: the surfaces each mounted
 * sonner's `<Toaster>` beside this provider until the shadcn kit was deleted,
 * and from then until this comment every `toast()` in the panel and the console
 * ran without rendering anything — a button that worked and said nothing.
 * Mount this once per application; a second one is a second stack.
 */
export function ToastProvider({ children }: { children?: ReactNode }) {
  const { pathname } = useLocation()
  useEffect(() => {
    sweepRoute(pathname)
  }, [pathname])
  return (
    <>
      {children}
      <Toaster
        theme='system'
        position='bottom-right'
        className='toaster group'
        icons={TOAST_ICONS}
        toastOptions={{ className: 'font-sans text-sm' }}
        style={TOAST_STYLE}
      />
    </>
  )
}
